import { Info, Loader2 } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'

import type { VatAlignmentDto } from './types'

interface QuoteLineVatMismatchBannerProps {
  alignment: VatAlignmentDto
  isAligning: boolean
  onOpenDialog: () => void
  onDismiss: () => void
}

// SOLY-VAT-001: signals the user that the current draft has many lines whose VAT does not
// match the project regime. Triggers when the BE flags `shouldRecommendAlignment` (≥ 50%
// of eligible lines mismatch, project default present, status Draft). Dismissal is per-
// session and clears if the mismatch is resolved by another action.
export function QuoteLineVatMismatchBanner({
  alignment,
  isAligning,
  onOpenDialog,
  onDismiss,
}: QuoteLineVatMismatchBannerProps) {
  const projectVat = alignment.projectDefaultVatPercent
  if (projectVat === null) return null

  return (
    <Alert
      variant="default"
      className="mt-3 border-amber-200 bg-amber-50/50"
      data-testid="quote-vat-mismatch-banner"
    >
      <Info className="h-4 w-4" />
      <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <span>
          <strong>{alignment.mismatchingLinesCount}</strong> sur{' '}
          <strong>{alignment.eligibleLinesCount}</strong> lignes éligibles ont une TVA différente du
          régime du projet ({projectVat}%).
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={onOpenDialog}
            disabled={isAligning}
            data-testid="quote-vat-mismatch-open-dialog-btn"
          >
            {isAligning ? (
              <>
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                Alignement…
              </>
            ) : (
              <>Aligner les TVA…</>
            )}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onDismiss}
            disabled={isAligning}
            data-testid="quote-vat-mismatch-dismiss-btn"
          >
            Ignorer
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  )
}
